import {
  BufferGeometry,
  EdgesGeometry,
  LineBasicMaterial,
  LineSegments,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  Shape,
  ShapeGeometry,
  Vector2,
  Vector3,
} from 'three';

import type { IMeshOptions, IMeshUserData, WidgetObject } from '@/types';

import { AnnoFactory } from './AnnoFactory';

// TODO：添加标签锚点
export class Box2d {
  /**
   * 创建矩形框对象
   * @param data [centerX, centerY, width, height] three.js 坐标系
   * @param options 配置项
   * @returns
   */
  static create(data: WidgetObject['data'], options: IMeshOptions): Mesh<ShapeGeometry, MeshBasicMaterial> {
    const { color, opacity: faceOpacity } = options;

    // 创建中心点位于坐标系原点，且宽高为 1 的标准矩形
    const rectShape = new Shape();
    const start = new Vector2(-0.5, 0.5);
    const next = new Vector2(0.5, -0.5);
    rectShape.moveTo(start.x, start.y).lineTo(next.x, start.y).lineTo(next.x, next.y).lineTo(start.x, next.y);

    const geometry = new ShapeGeometry(rectShape);
    const box2d = new Mesh(
      geometry,
      new MeshBasicMaterial({
        color,
        transparent: true,
        opacity: faceOpacity,
      })
    );
    box2d.name = box2d.uuid;
    box2d.userData = { type: 'box2d', color, opacity: faceOpacity, anchor: [0, 0, 0] } satisfies IMeshUserData;

    // 受照相机可视范围限制，z轴值不能超过100
    box2d.position.setZ(AnnoFactory.generateObjectZ(box2d));
    box2d.scale.setZ(1);

    Box2d.update(box2d, data);

    const border = new LineSegments(
      new EdgesGeometry(geometry),
      new LineBasicMaterial({
        linewidth: 1,
        color,
      })
    );
    box2d.add(border);

    return box2d;
  }

  /**
   * 更新矩形框
   * @param target 更新目标
   * @param data [centerX, centerY, width, height] three.js 坐标系
   */
  static update(target: Mesh<ShapeGeometry, MeshBasicMaterial>, data: WidgetObject['data']) {
    const [x, y, width, height] = data;
    if (typeof x === 'number' && typeof y === 'number') {
      target.position.setX(x);
      target.position.setY(y);
    }
    if (typeof width === 'number' && typeof height === 'number') {
      target.scale.setX(width);
      target.scale.setY(height);
    }
    Box2d.updateAnchor(target);
  }

  /**
   * 根据 widget 生成 threejs 对象
   * @param name 唯一ID
   * @param widget [centerX, centerY, width, height] 文件坐标系
   * @param matrix 从图片文件坐标系 到 three.js 坐标系的转换矩阵【默认仅包含平移、缩放，不包含旋转】
   * @param options
   * @returns
   */
  static createFromWidget(
    name: string,
    widget: WidgetObject,
    matrix: Matrix4,
    options: IMeshOptions
  ): Mesh<ShapeGeometry, MeshBasicMaterial> {
    const box2d = Box2d.create(Box2d.formatWidgetData(widget, matrix), options);
    box2d.name = name;

    return box2d;
  }

  /**
   * 由 AnnoInstanceItem.widget 数据更新立体框
   * @param target 更新目标
   * @param widget [centerX, centerY, width, height] 文件坐标系
   * @param matrix 从图片文件坐标系 到 three.js 坐标系的转换矩阵【默认仅包含平移、缩放，不包含旋转】
   */
  static updateFromWidget(target: Mesh<ShapeGeometry, MeshBasicMaterial>, widget: WidgetObject, matrix: Matrix4) {
    Box2d.update(target, Box2d.formatWidgetData(widget, matrix));
  }

  /**
   * 导出Widget格式数据
   * @param target
   * @param matrix three.js 3D 坐标系 到 文件（点云/图片）坐标系的转换矩阵,【默认仅包含平移、缩放，不包含旋转】
   * @returns
   */
  static transOutWidget(target: Mesh<BufferGeometry, MeshBasicMaterial>, matrix: Matrix4): WidgetObject {
    const { position, scale } = target;

    const [rx, ry] = position.clone().applyMatrix4(matrix).toArray();

    // 由于矩阵不包含旋转数据，故可直接取 m[0][0], m[1][1] 的值分别作为 x, y 轴的缩放分量
    const sx = Math.abs(matrix.elements[0]),
      sy = Math.abs(matrix.elements[5]);

    return {
      name: 'box2d',
      data: [rx, ry, scale.x * sx, scale.y * sy],
    };
  }

  /**
   * 处理从文件坐标系到three.js 坐标系的数据转换
   * @param widget [centerX, centerY, width, height] 文件坐标系
   * @param matrix 从图片文件坐标系 到 three.js 坐标系的转换矩阵【默认仅包含平移、缩放，不包含旋转】
   * @returns data [centerX, centerY, width, height] three.js 坐标系
   */
  static formatWidgetData(widget: WidgetObject, matrix: Matrix4) {
    const {
      data: [x, y, w, h],
    } = widget;

    const [rx, ry] = new Vector3(x, y, 0).applyMatrix4(matrix).toArray();

    // 由于矩阵不包含旋转数据，故可直接取 m[0][0], m[1][1] 的值分别作为 x, y 轴的缩放分量
    const sx = Math.abs(matrix.elements[0]),
      sy = Math.abs(matrix.elements[5]);

    return [rx, ry, w * sx, h * sy];
  }

  static changeColor(target: Mesh<ShapeGeometry, MeshBasicMaterial>, colorHex: string, refreshData = true) {
    target.traverse((object) => {
      if (
        'material' in object &&
        (object.material instanceof LineBasicMaterial || object.material instanceof MeshBasicMaterial)
      ) {
        object.material.color.set(colorHex);
      }
    });

    if (refreshData) {
      target.userData.color = colorHex;
    }
  }

  /**
   * 设置锚点
   * @param target
   */
  static updateAnchor(target: Mesh<BufferGeometry, MeshBasicMaterial>) {
    const { position, scale } = target;
    target.userData.anchor = [position.x - scale.x / 2, position.y + scale.y / 2, 0];
  }
}
